import type {
  ClientToServer,
  CreateChannelRequest,
  CreateChannelResponse,
  EncryptedEnvelope,
  JoinChannelRequest,
  JoinChannelResponse,
  KeyDistribution,
  LatestResponse,
  PushResponse,
  ServerToClient,
} from "@zeroclipboard/shared";

async function parseJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok) {
    throw new Error((data as { error?: string }).error || `HTTP ${res.status}`);
  }
  return data as T;
}

/** Thin REST client for the untrusted relay. Sends only ciphertext + metadata. */
export class ApiClient {
  constructor(private readonly baseUrl = "") {}

  private url(path: string): string {
    return `${this.baseUrl}${path}`;
  }

  async createChannel(
    req: CreateChannelRequest
  ): Promise<CreateChannelResponse> {
    const res = await fetch(this.url("/api/channel/create"), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(req),
    });
    return parseJson(res);
  }

  async join(req: JoinChannelRequest): Promise<JoinChannelResponse> {
    const res = await fetch(this.url("/api/channel/join"), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(req),
    });
    return parseJson(res);
  }

  async getLatest(channelId: string): Promise<LatestResponse> {
    const res = await fetch(
      this.url(`/api/channel/${encodeURIComponent(channelId)}/latest`)
    );
    return parseJson(res);
  }

  async push(
    channelId: string,
    envelope: EncryptedEnvelope,
    keyDistribution?: KeyDistribution
  ): Promise<PushResponse> {
    const body: {
      envelope: EncryptedEnvelope;
      keyDistribution?: KeyDistribution;
    } = {
      envelope,
    };
    if (keyDistribution) body.keyDistribution = keyDistribution;
    const res = await fetch(
      this.url(`/api/channel/${encodeURIComponent(channelId)}/push`),
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      }
    );
    return parseJson(res);
  }

  /** Creator-authorized manual expiry (proof = SHA-256(channelId‖secret)). */
  async expireChannel(channelId: string, pairingProof: string): Promise<void> {
    const res = await fetch(
      this.url(`/api/channel/${encodeURIComponent(channelId)}`),
      {
        method: "DELETE",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ pairingProof }),
      }
    );
    await parseJson(res);
  }
}

export interface WsClientHandlers {
  onMessage(msg: ServerToClient): void;
  onOpen?(): void;
  onClose?(): void;
}

/**
 * Auto-reconnecting WebSocket client (push model). Falls back to HTTP polling
 * is handled by the caller when {@link WsClient} never reaches the open state.
 */
export class WsClient {
  private ws: WebSocket | null = null;
  private closedByUser = false;
  private reconnectDelay = 1000;
  private everOpen = false;

  constructor(
    private readonly baseUrl: string,
    private readonly channelId: string,
    private readonly deviceId: string,
    private readonly handlers: WsClientHandlers
  ) {}

  get hasEverConnected(): boolean {
    return this.everOpen;
  }

  connect(): void {
    this.closedByUser = false;
    const url = this.wsUrl();
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      this.everOpen = true;
      this.reconnectDelay = 1000;
      this.send({
        t: "hello",
        channelId: this.channelId,
        deviceId: this.deviceId,
      });
      this.handlers.onOpen?.();
    };
    ws.onmessage = (ev) => {
      try {
        this.handlers.onMessage(JSON.parse(String(ev.data)) as ServerToClient);
      } catch {
        /* ignore malformed frame */
      }
    };
    ws.onclose = () => {
      this.handlers.onClose?.();
      if (!this.closedByUser) this.scheduleReconnect();
    };
    ws.onerror = () => ws.close();
  }

  send(msg: ClientToServer): boolean {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
      return true;
    }
    return false;
  }

  close(): void {
    this.closedByUser = true;
    this.ws?.close();
    this.ws = null;
  }

  private scheduleReconnect(): void {
    const delay = this.reconnectDelay;
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, 15000);
    setTimeout(() => {
      if (!this.closedByUser) this.connect();
    }, delay);
  }

  private wsUrl(): string {
    let origin = this.baseUrl;
    if (!origin) origin = location.origin;
    const u = new URL(origin);
    u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
    u.pathname = "/ws";
    u.search = `?channel=${encodeURIComponent(this.channelId)}&device=${encodeURIComponent(this.deviceId)}`;
    return u.toString();
  }
}
